import { createContext, type ComponentChildren, type VNode } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';

/**
 * Lightweight toast queue. Replaces the legacy SectionFlash inline
 * banner with a corner-anchored stack:
 *
 *   • top-right anchored, fixed z-50
 *   • max 3 visible at a time; older toasts fall off the bottom
 *   • auto-dismiss after 5s; manual dismiss via the × glyph
 *   • two variants - success (state-ready) / error (state-failed)
 *
 * Wrap the root in <ToastProvider/>, then `const { push } = useToast()`
 * from any descendant.
 */
export type ToastVariant = 'success' | 'error';

interface ToastItem {
    id: number;
    variant: ToastVariant;
    message: ComponentChildren;
}

interface ToastContextValue {
    push: (variant: ToastVariant, message: ComponentChildren) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 5_000;
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ComponentChildren }) {
    const [items, setItems] = useState<ToastItem[]>([]);
    const seq = useRef(0);

    const dismiss = useCallback((id: number) => {
        setItems((curr) => curr.filter((t) => t.id !== id));
    }, []);

    const push = useCallback(
        (variant: ToastVariant, message: ComponentChildren) => {
            seq.current += 1;
            const id = seq.current;
            setItems((curr) => {
                const next = [...curr, { id, variant, message }];
                // Trim to MAX_VISIBLE - drop oldest first.
                return next.length > MAX_VISIBLE ? next.slice(-MAX_VISIBLE) : next;
            });
            window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
        },
        [dismiss],
    );

    const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastViewport items={items} onDismiss={dismiss} />
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // Defensive: in non-admin contexts (e.g., the catalog routes)
        // there's no provider mounted. Return a no-op so descendants
        // can call useToast() unconditionally without crashing.
        return { push: () => {} };
    }
    return ctx;
}

function ToastViewport({
    items,
    onDismiss,
}: {
    items: ToastItem[];
    onDismiss: (id: number) => void;
}): VNode | null {
    if (items.length === 0) return null;
    return (
        <div
            aria-live="polite"
            class="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2"
        >
            {items.map((t) => (
                <ToastCard key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
            ))}
        </div>
    );
}

function ToastCard({
    item,
    onDismiss,
}: {
    item: ToastItem;
    onDismiss: () => void;
}) {
    const tone =
        item.variant === 'success'
            ? 'border-state-ready/40 bg-state-ready/10 text-fg-primary'
            : 'border-state-failed/40 bg-state-failed/10 text-fg-primary';
    const dotTone = item.variant === 'success' ? 'bg-state-ready' : 'bg-state-failed';

    // Animate in once on mount.
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const id = window.requestAnimationFrame(() => setEntered(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    return (
        <div
            role="status"
            class={`pointer-events-auto flex items-start gap-3 rounded-md border ${tone} px-4 py-3 shadow-lg backdrop-blur-sm transition-all duration-200 ${
                entered ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
            }`}
        >
            <span
                aria-hidden="true"
                class={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotTone}`}
            />
            <div class="min-w-0 flex-1 text-sm">{item.message}</div>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                class="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-fg-muted transition-colors hover:bg-accent-glow hover:text-fg-primary"
            >
                <span aria-hidden="true" class="text-base leading-none">×</span>
            </button>
        </div>
    );
}
